import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "hooks/useAuth";
import { horusApi } from "services/api/horus";
import { Row, Col, Carousel } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import {
  getClientPalette,
  defaultPalette,
} from "../../../../../config/clientPalettes";
import { useGroupedItems } from "hooks/useGroupedItems";
import BreakdownByAccountingMethod from "./BreakdownByAccountingMethod";
import BreakdownByAmortizationMethod from "./BreakdownByAmortizationMethod";
import BreakdownByInterestType from "./BreakdownByInterestType";
import WaterfallEffects from "./WaterfallEffects";
import MarginalCumulativeEffects from "./MarginalCumulativeEffects";
import EmptyCardVariation from "components/common/pyramid/EmptyCardVariation";
import TopPurchaseEffects from "./TopPurchaseEffects";
import TopSaleEffects from "./TopSaleEffects";
import TopRedemptionEffects from "./TopRedemptionEffects";
import TopCashflowEffects from "./TopCashflowEffects";
import TopActuarialRateEffects from "./TopActuarialRateEffects";

// Carousel section for the Amortization departure, arrival and variation
const CarouselSection = ({
  amortizationDepartureData,
  amortizationArrivalData,
  amortizationVariationData,
}) => {
  return (
    <>
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel
            variant="dark"
            interval={3000} // Auto-rotate every 3 seconds
            controls={true} // Show navigation controls
            indicators={false} // Hide slide indicators
          >
            <Carousel.Item>
              <EmptyCard chartData={amortizationDepartureData} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard badgeColor="info" chartData={amortizationArrivalData} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>

      <Col md={4}>
        <PyramidCard className="h-100">
          <EmptyCardVariation eclData={amortizationVariationData} />
        </PyramidCard>
      </Col>
    </>
  );
};

const VariationAnalysisCharts = () => {
  const { account, companyList, accessToken, userList } = useAuth();
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const [chartData, setChartData] = useState({
    amortizationDepartureData: null,
    amortizationArrivalData: null,
    amortizationVariationData: null,
    breakdownByAccountingMethodData: null,
    breakdownByAmortizationMethodData: null,
    breakdownByInterestTypeData: null,
    topPurchaseEffectsData: null,
    topSaleEffectsData: null,
    topRedemptionEffectsData: null,
    topCashflowEffectsData: null,
    topActuarialRateEffectsData: null,
    waterfallData: [],
    marginalCumulativeEffectsData: [],
  });

  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  const [variationAnalyses, setVariationAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const api = horusApi();

  const groupedVariationAnalyses = useGroupedItems(
    variationAnalyses,
    userList,
    companyList
  );

  useEffect(() => {
    const fetchVariationAnalyses = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getVariations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const analyses = Array.isArray(data) ? data : data.result || [];

          const sortedAnalyses = [...analyses].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setVariationAnalyses(sortedAnalyses);

          if (sortedAnalyses.length > 0) {
            setSelectedAnalysis(sortedAnalyses[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVariationAnalyses();
  }, [accessToken]);

  useEffect(() => {
    const fetchVariationCharts = async () => {
      if (!selectedAnalysis || !accessToken) return;

      try {
        const data = await api.getVariationCharts(selectedAnalysis);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          const transformedData = {
            amortizationDepartureData: result.find((chart) => chart.Name === "SumAmoDeparture")?.Data[0] || null,
            amortizationArrivalData: result.find((chart) => chart.Name === "SumAmoArrival")?.Data[0] || null,
            amortizationVariationData: result.find((chart) => chart.Name === "SumAmoVariation")?.Data[0] || null,
            breakdownByAccountingMethodData: result.find((chart) => chart.Name === "BreakdownByAccountingMethod")?.Data || [],
            breakdownByAmortizationMethodData: result.find((chart) => chart.Name === "BreakdownByAmortizationMethod")?.Data || [],
            breakdownByInterestTypeData: result.find((chart) => chart.Name === "BreakdownByInterestType")?.Data || [],
            topPurchaseEffectsData: result.find((chart) => chart.Name === "TopPurchaseEffect")?.Data || [],
            topSaleEffectsData: result.find((chart) => chart.Name === "TopSaleEffect")?.Data || [],
            topRedemptionEffectsData: result.find((chart) => chart.Name === "TopRedemptionEffect")?.Data || [],
            topCashflowEffectsData: result.find((chart) => chart.Name === "TopCashflowEffect")?.Data || [],
            topActuarialRateEffectsData: result.find((chart) => chart.Name === "TopActuarialRateEffect")?.Data || [],
            waterfallData: result.find((chart) => chart.Name === "WaterfallEffects")?.Data || [],
            marginalCumulativeEffectsData: result.find((chart) => chart.Name === "MarginalCumulativeEffect")?.Data || [],
          };
          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error in the response:", error);
      }
    };

    fetchVariationCharts();
  }, [selectedAnalysis, accessToken]);

  return (
    <div>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Choose your variation
              </div>
              <SelectBox
                dataSource={groupedVariationAnalyses}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedAnalysis}
                onValueChanged={(e) => setSelectedAnalysis(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>

        <CarouselSection
          amortizationDepartureData={chartData.amortizationDepartureData}
          amortizationArrivalData={chartData.amortizationArrivalData}
          amortizationVariationData={chartData.amortizationVariationData}
        />
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <BreakdownByAccountingMethod
            data={chartData.breakdownByAccountingMethodData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <BreakdownByAmortizationMethod
            data={chartData.breakdownByAmortizationMethodData}
            palette={currentPalette}
          />
        </Col>
        <Col md={6}>
          <BreakdownByInterestType
            data={chartData.breakdownByInterestTypeData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <WaterfallEffects
            data={chartData.waterfallData}
            palette={currentPalette}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <MarginalCumulativeEffects data={chartData.marginalCumulativeEffectsData} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopPurchaseEffects data={chartData.topPurchaseEffectsData} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopSaleEffects data={chartData.topSaleEffectsData} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopRedemptionEffects data={chartData.topRedemptionEffectsData} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopCashflowEffects data={chartData.topCashflowEffectsData} />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={12}>
          <TopActuarialRateEffects data={chartData.topActuarialRateEffectsData} />
        </Col>
      </Row>
    </div>
  );
};

export default VariationAnalysisCharts;
